const SAMPLE_RATE = 16000;
const WINDOW_MS = 20;
const WINDOW_SIZE = (SAMPLE_RATE * WINDOW_MS) / 1000;
const RMS_SPEECH_THRESHOLD = 0.012;
const MIN_SPEECH_MS = 400;
const LEAD_PAD_MS = 120;
const TAIL_PAD_MS = 200;

/**
 * Trim leading/trailing silence from a PCM buffer and decide whether it
 * contains enough speech to be worth transcribing. Keeps a small amount of
 * padding around the speech region so consonants at the edges aren't clipped.
 *
 * Returns null if the clip is effectively silent (e.g. mis-click, keystroke).
 */
export function trimSilence(pcm: Float32Array): Float32Array | null {
  if (pcm.length < WINDOW_SIZE) return null;

  const windowCount = Math.floor(pcm.length / WINDOW_SIZE);
  let firstSpeech = -1;
  let lastSpeech = -1;

  for (let w = 0; w < windowCount; w++) {
    const start = w * WINDOW_SIZE;
    let sumSq = 0;
    for (let i = 0; i < WINDOW_SIZE; i++) {
      const s = pcm[start + i];
      sumSq += s * s;
    }
    const rms = Math.sqrt(sumSq / WINDOW_SIZE);
    if (rms >= RMS_SPEECH_THRESHOLD) {
      if (firstSpeech === -1) firstSpeech = w;
      lastSpeech = w;
    }
  }

  if (firstSpeech === -1) return null;

  const speechWindows = lastSpeech - firstSpeech + 1;
  if (speechWindows * WINDOW_MS < MIN_SPEECH_MS) return null;

  const leadPadWindows = Math.ceil(LEAD_PAD_MS / WINDOW_MS);
  const tailPadWindows = Math.ceil(TAIL_PAD_MS / WINDOW_MS);
  const startWindow = Math.max(0, firstSpeech - leadPadWindows);
  const endWindow = Math.min(windowCount, lastSpeech + 1 + tailPadWindows);

  return pcm.subarray(startWindow * WINDOW_SIZE, endWindow * WINDOW_SIZE);
}
